import { useEffect, useRef, useState } from "react";

export function loadStoredPx(key: string, fallback: number, min: number, max: number): number {
  try {
    const n = Number(localStorage.getItem(key));
    if (Number.isFinite(n)) return Math.max(min, Math.min(max, Math.round(n)));
  } catch {
    /* */
  }
  return fallback;
}

export function saveStoredPx(key: string, n: number): void {
  try {
    localStorage.setItem(key, String(Math.round(n)));
  } catch {
    /* */
  }
}

type Props = {
  axis?: "y" | "x";
  value: number;
  min: number;
  max: number;
  getMax?: () => number;
  onChange: (n: number) => void;
  onReset?: () => void;
  label: string;
  invert?: boolean;
  className?: string;
};

export function SplitHandle({
  axis = "y",
  value,
  min,
  max,
  getMax,
  onChange,
  onReset,
  label,
  invert = true,
  className = "",
}: Props) {
  const start = useRef<{ pos: number; val: number } | null>(null);
  const [hot, setHot] = useState(false);
  const latest = useRef({ axis, invert, min, max, getMax, onChange, value });
  latest.current = { axis, invert, min, max, getMax, onChange, value };

  const apply = (clientX: number, clientY: number) => {
    const d = start.current;
    if (!d) return;
    const { axis: ax, invert: inv, min: lo, max: cap, getMax: gm, onChange: set } = latest.current;
    const hi = Math.max(lo, gm?.() ?? cap);
    const pos = ax === "y" ? clientY : clientX;
    const next = d.val + (inv ? -(pos - d.pos) : pos - d.pos);
    set(Math.max(lo, Math.min(hi, Math.round(next))));
  };

  useEffect(() => {
    if (!hot) return;
    const move = (e: PointerEvent) => apply(e.clientX, e.clientY);
    const up = () => {
      start.current = null;
      setHot(false);
      document.body.classList.remove("mf-split-drag", "mf-split-drag-x");
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [hot]);

  return (
    <div
      className={`mf-split ${axis}${hot ? " drag" : ""}${className ? ` ${className}` : ""}`}
      role="separator"
      aria-label={label}
      aria-orientation={axis === "y" ? "horizontal" : "vertical"}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        start.current = { pos: axis === "y" ? e.clientY : e.clientX, val: latest.current.value };
        setHot(true);
        document.body.classList.add(axis === "x" ? "mf-split-drag-x" : "mf-split-drag");
      }}
      onPointerMove={(e) => apply(e.clientX, e.clientY)}
      onDoubleClick={() => onReset?.()}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 32 : 12;
        const hi = Math.max(min, getMax?.() ?? max);
        if (axis === "y") {
          const grow = invert ? e.key === "ArrowUp" : e.key === "ArrowDown";
          const shrink = invert ? e.key === "ArrowDown" : e.key === "ArrowUp";
          if (grow) {
            e.preventDefault();
            onChange(Math.min(hi, value + step));
          }
          if (shrink) {
            e.preventDefault();
            onChange(Math.max(min, value - step));
          }
        } else {
          const grow = invert ? e.key === "ArrowLeft" : e.key === "ArrowRight";
          const shrink = invert ? e.key === "ArrowRight" : e.key === "ArrowLeft";
          if (grow) {
            e.preventDefault();
            onChange(Math.min(hi, value + step));
          }
          if (shrink) {
            e.preventDefault();
            onChange(Math.max(min, value - step));
          }
        }
      }}
    />
  );
}
